// Save-time weight outlier detection. Every logger imports detectOutliers
// from here so the prompt logic stays in lockstep (the legacy web's
// WorkoutLog.tsx was one of them, removed with RW-14, readable at 7ae8e35e).
//
// Two tiers of detection:
//
//   1. History-based: when the lifter has done this exercise before,
//      flag any weight that exceeds 3× the recent best. Catches the
//      decimal-shift typo (54.5 → 545) that the original detector was
//      built around.
//
//   2. Absolute equipment ceiling: when there's no history (brand-new
//      exercise, fresh user, edit mode without aggregation), fall back
//      to a per-equipment-kind sanity ceiling. Without this fallback a
//      999 kg dumbbell row on a never-logged exercise slid through —
//      the inbox bug 2026-05-12 explicitly called out.
//
// Bodyweight entries are skipped: extra/vest values are small and
// noisy, history rarely settles. Per-set bilateral toggling
// (loadedSides field) doesn't change the comparison: weight stays
// per-implement on both sides of the toggle, so the per-side ceiling
// + per-side history both line up.

import type { EquipmentKind } from "./schemas/muscle.js";
import { computeE1rmKg } from "./pr.js";

// Per-implement ceilings (kg). Tuned for the family-app scale: well
// above any realistic single-side load, low enough to catch the typo
// (999) on a fresh exercise. Numbers documented per kind so flips are
// traceable; revisit if a real lifter hits a ceiling.
//
// Loaded carry: the field is total external load, not per-side, so
// gets its own ceiling separate from the per-implement table.
const EQUIPMENT_CEILING_KG: Record<EquipmentKind, number> = {
  barbell: 400,
  dumbbell: 80,
  kettlebell: 80,
  machine: 500,
  cable: 200,
  band: 100,
  // Bodyweight entries are skipped before the ceiling lookup (extra/
  // vest values are too noisy to compare). Kept here so the Record
  // type stays exhaustive.
  bodyweight: 100,
  // Cardio equipment (1.62.0). Outlier checks fire on the `weight`
  // field of a cardio set when present (loaded vest, sled load);
  // ceilings reflect realistic external loads, not the machine
  // itself. Most cardio rows omit `weight` and skip the ceiling
  // lookup entirely; these values exist so the Record type stays
  // exhaustive after the EquipmentKind enum widened.
  treadmill: 50,
  "exercise-bike": 50,
  elliptical: 50,
  rower: 50,
  "stair-climber": 50,
  "jump-rope": 30,
  "battle-ropes": 30,
  sled: 400,
  other: 300,
};

// Fallback when the catalog row carries no equipment kind. Generous
// enough to not bother the lifter on a typical day, small enough to
// catch the obvious typo.
const DEFAULT_CEILING_KG = 300;

const LOADED_CARRY_CEILING_KG = 300;

const HISTORY_RATIO = 3;

// Rep count above which a logged set on a loaded lift is implausible
// enough to warrant a save-time prompt. Coach data-quality gap: Leonardo
// logged a 200-rep leg curl that flowed straight into e1RM/volume. 50 is
// generous for any barbell/machine work; high-rep bodyweight (push-ups,
// calf raises) is exempt — bodyweight entries are skipped before this
// check, so the ceiling never bothers legitimate calisthenics volume.
const REPS_CEILING = 50;

// Estimated-1RM jump ratio. The history weight check (HISTORY_RATIO = 3×)
// is deliberately loose — it only catches the decimal-shift typo
// (54.5 → 545). A moderate over-weight typo (100 → 160 at 5 reps) stays
// under 3× but inflates the set's e1RM by ~60%, and that inflated e1RM is
// stamped on the row (setFactory computeE1rmKg) → it poisons the dynamic
// MAX (the auto-derived one), the post-luglio differentiator. A single
// set whose e1RM exceeds 1.5× the lifter's recent best e1RM for that lift
// is implausible in one session (real session-over-session gains are a few
// %); it's the typo signature. Only fires when there IS e1RM history.
const E1RM_JUMP_RATIO = 1.5;

// Per-implement entry-mistake band (IB-8 Slice 2, Antenore 2026-07-14:
// «preferisco un warning al salvataggio che chiede conferma»). On an
// exercise the app counts ×2 (loadedSides=2), a weight that lands near
// DOUBLE the lifter's recent best has the signature of "entered the
// TOTAL of both implements instead of one" — the exact mistake that
// double-counted Leonardo's history. Bounded band: below 1.6× is a
// plausible PR jump, above 2.8× the decimal-shift check (3×) owns it.
// Confirm-only, never blocks the save.
const PER_IMPLEMENT_DOUBLE_MIN = 1.6;
const PER_IMPLEMENT_DOUBLE_MAX = 2.8;

export type OutlierFinding = {
  entryIdx: number;
  setIdx: number;
  exerciseName: string;
  weightKg: number;
  /** History-based: prior best. Absolute: equipment ceiling. For the
   *  reps / zero-weight reasons this is the ceiling / 0 and is not shown
   *  by the prompt copy (those branches render `reps` instead). */
  expectedMaxKg: number;
  /** The flagged set's rep count. Present for the "reps" / "zero-weight"
   *  reasons (drives their prompt copy); absent for the weight tiers. */
  reps?: number;
  /** Computed e1RM of the flagged set. Present only for the "e1rm-jump"
   *  reason; `expectedMaxKg` carries the recent-best e1RM it's compared
   *  against. */
  e1rmKg?: number;
  /** Why this set was flagged.
   *   - history / absolute: weight too high (decimal-shift typo /
   *     equipment ceiling on a no-history exercise).
   *   - reps: rep count above REPS_CEILING on a loaded lift.
   *   - zero-weight: reps logged with no/zero load on a loaded lift.
   *   - e1rm-jump: the set's implied 1RM exceeds 1.5× the recent best —
   *     a moderate over-weight typo the loose 3× weight gate misses.
   *   - per-implement-double: on a ×2-counted exercise the weight is
   *     ~double the recent best — the "entered the total of both
   *     dumbbells" signature (IB-8). */
  reason:
    | "history"
    | "absolute"
    | "reps"
    | "zero-weight"
    | "e1rm-jump"
    | "per-implement-double";
};

/** Per-entry input shape. Both clients project their internal
 *  representation into this before calling the helper. The helper
 *  doesn't care whether the source was DraftEntry, WorkoutEntry, or
 *  anything else. */
export type OutlierEntry = {
  exerciseId: string;
  exerciseName: string;
  isBodyweight: boolean;
  /** Equipment kind from the catalog row, when available. Drives the
   *  no-history ceiling. Undefined falls back to DEFAULT_CEILING_KG. */
  equipment?: EquipmentKind;
  /** Cardio entry (treadmill/bike/row/jump-rope/…). Skips the reps and
   *  zero-weight checks — a cardio row legitimately has high "reps"
   *  (jump rope) or no weight. The weight-tier checks still run (a
   *  loaded vest/sled outlier is real). Optional so callers that don't
   *  pass it (web) keep the prior behaviour. */
  isCardio?: boolean;
  /** Entry-level laterality (1|2). When 2 the per-implement-double
   *  check runs on the entry's weighted sets. Optional so existing
   *  callers keep the prior behaviour. */
  loadedSides?: 1 | 2;
  /** Per-set rows. `weightKg` is the canonical kg; the caller has
   *  already converted from lb if needed. `kind` distinguishes the
   *  loaded-carry ceiling from the per-implement one. `reps` is the
   *  logged rep count (optional — callers that don't project it skip
   *  the reps / zero-weight checks). */
  sets: ReadonlyArray<{
    weightKg: number | null;
    reps?: number | null;
    kind: "weighted" | "bodyweight" | "loaded-carry" | "p1rm";
  }>;
};

export type OutlierInput = {
  entries: ReadonlyArray<OutlierEntry>;
  /** Map of exerciseId → recent best weighted kg, derived by the
   *  caller from the user's recent history (workouts cache on web,
   *  prHistory aggregate on mobile). Missing slug = no history,
   *  triggers the absolute fallback. */
  recentMaxKgBySlug: ReadonlyMap<string, number>;
  /** Map of exerciseId → recent best estimated-1RM kg (max epley over the
   *  caller's history, gated by E1RM_REP_CAP). Drives the e1rm-jump check.
   *  Optional: callers that don't pass it skip that check (back-compat).
   *  Missing slug = no e1RM history for that lift → check skipped. */
  recentE1rmKgBySlug?: ReadonlyMap<string, number>;
};

export const detectOutliers = (input: OutlierInput): OutlierFinding[] => {
  const out: OutlierFinding[] = [];
  for (let entryIdx = 0; entryIdx < input.entries.length; entryIdx++) {
    const entry = input.entries[entryIdx];
    if (!entry || entry.isBodyweight) continue;
    const historyMax = input.recentMaxKgBySlug.get(entry.exerciseId) ?? 0;
    for (let setIdx = 0; setIdx < entry.sets.length; setIdx++) {
      const s = entry.sets[setIdx];
      if (!s) continue;
      const w = s.weightKg;
      const reps = s.reps ?? null;
      // Data-quality checks (loaded lifts only — cardio rows have
      // legitimate high reps / no weight). Additive: they run before the
      // weight-tier checks and don't alter that path.
      if (!entry.isCardio && reps !== null && Number.isFinite(reps)) {
        if (reps > REPS_CEILING) {
          out.push({
            entryIdx,
            setIdx,
            exerciseName: entry.exerciseName,
            weightKg: w ?? 0,
            expectedMaxKg: REPS_CEILING,
            reps,
            reason: "reps",
          });
        }
        // Reps logged on a loaded lift but the load is missing/zero —
        // the "0 kg set" that skews volume. Only meaningful when work
        // was actually done (reps > 0). Mutually exclusive with the reps
        // check above: an absurd-rep set with no weight reports only the
        // rep count (one finding per set keeps the save prompt readable).
        else if (reps > 0 && (w === null || !Number.isFinite(w) || w <= 0)) {
          out.push({
            entryIdx,
            setIdx,
            exerciseName: entry.exerciseName,
            weightKg: 0,
            expectedMaxKg: 0,
            reps,
            reason: "zero-weight",
          });
        }
      }
      if (w === null || !Number.isFinite(w) || w <= 0) continue;
      // Tier 1: history-based. Cheap to evaluate and catches the
      // decimal-shift typo against the lifter's own track record.
      if (historyMax > 0) {
        if (w > historyMax * HISTORY_RATIO) {
          out.push({
            entryIdx,
            setIdx,
            exerciseName: entry.exerciseName,
            weightKg: w,
            expectedMaxKg: historyMax,
            reason: "history",
          });
        } else if (
          entry.loadedSides === 2 &&
          s.kind === "weighted" &&
          w >= historyMax * PER_IMPLEMENT_DOUBLE_MIN &&
          w <= historyMax * PER_IMPLEMENT_DOUBLE_MAX
        ) {
          // ~Double the recent best on a ×2-counted lift: the "entered
          // the total of both dumbbells" signature. Asks, never blocks —
          // a genuine near-2× PR survives the confirm.
          out.push({
            entryIdx,
            setIdx,
            exerciseName: entry.exerciseName,
            weightKg: w,
            expectedMaxKg: historyMax,
            reason: "per-implement-double",
          });
        } else {
          // The gross 3× weight rule didn't fire. Check the subtler
          // e1RM jump: a moderate over-weight typo (100 → 160 at 5 reps)
          // stays under 3× but inflates the implied 1RM, which gets
          // stamped on the row and poisons the dynamic MAX. Only when
          // the set is e1RM-eligible (reps in cap) AND we have e1RM
          // history for this lift. One finding per set in this tier.
          const setE1rm =
            reps !== null && Number.isFinite(reps)
              ? computeE1rmKg({ kind: "weighted", reps, weight: w })
              : null;
          const bestE1rm =
            input.recentE1rmKgBySlug?.get(entry.exerciseId) ?? 0;
          if (setE1rm !== null && bestE1rm > 0 && setE1rm > bestE1rm * E1RM_JUMP_RATIO) {
            out.push({
              entryIdx,
              setIdx,
              exerciseName: entry.exerciseName,
              weightKg: w,
              expectedMaxKg: bestE1rm,
              e1rmKg: setE1rm,
              reason: "e1rm-jump",
            });
          }
        }
        // History hit — don't double-fire on the absolute ceiling
        // even if the weight also exceeds it. The history comparison
        // is the more informative prompt.
        continue;
      }
      // Tier 2: no history → absolute ceiling. Different ceiling for
      // loaded-carry (total load) vs the per-implement work.
      const equipmentCeiling =
        entry.equipment !== undefined
          ? (EQUIPMENT_CEILING_KG[entry.equipment] ?? DEFAULT_CEILING_KG)
          : DEFAULT_CEILING_KG;
      const ceiling =
        s.kind === "loaded-carry" ? LOADED_CARRY_CEILING_KG : equipmentCeiling;
      if (w > ceiling) {
        out.push({
          entryIdx,
          setIdx,
          exerciseName: entry.exerciseName,
          weightKg: w,
          expectedMaxKg: ceiling,
          reason: "absolute",
        });
      }
    }
  }
  return out;
};

export {
  EQUIPMENT_CEILING_KG,
  DEFAULT_CEILING_KG,
  LOADED_CARRY_CEILING_KG,
  HISTORY_RATIO,
  REPS_CEILING,
  E1RM_JUMP_RATIO,
};
